// Estilos globales y de librerías
// Eliminamos las importaciones de PDF Viewer
import "react-phone-number-input/style.css";
import "react-toastify/dist/ReactToastify.css";
import "../styles/globals.css";

// Dependencias de React y Next.js
import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
// Proveedores y componentes de terceros
import { UserProvider, useUser } from "@auth0/nextjs-auth0/client";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { ToastContainer, toast } from "react-toastify";

// Componentes y contextos propios
import LoadingSpinner from "../Components/LoadingSpinner";
import { AppWrapper } from "../context/state";
import { Button } from "../Components/Common/Button";

// Integraciones de monitoreo y análisis
import Bugsnag from "@bugsnag/js";
import BugsnagPluginReact from "@bugsnag/plugin-react";
import BugsnagPerformance from "@bugsnag/browser-performance";
import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

// Constantes de configuración
const AMPLITUDE_API_KEY = "dee622db9fc22974a61a156d496d9851";

// Inicialización de terceros - diferida para no bloquear renderizado
// Hook para controlar el estado de carga en la navegación
function useRouteLoading() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleStart = () => setIsLoading(true);
    const handleComplete = () => setIsLoading(false);

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router.events]);

  return isLoading;
}

// Hook para cargar Bugsnag con retraso
function useBugsnagInitialization() {
  const initialized = useRef(false);

  useEffect(() => {
    // Esperar 3 segundos para dar prioridad a la carga e interactividad
    const timer = setTimeout(async () => {
      if (!initialized.current) {
        try {
          const Bugsnag = (await import('@bugsnag/js')).default;
          const BugsnagPluginReact = (await import('@bugsnag/plugin-react')).default;
          const BugsnagPerformance = (await import('@bugsnag/browser-performance')).default;

          if (!Bugsnag._client) {
            Bugsnag.start({
              apiKey: "1a657fa52ee13ce9f07960a1ed527d51",
              plugins: [new BugsnagPluginReact()],
            });
            BugsnagPerformance.start({ apiKey: "1a657fa52ee13ce9f07960a1ed527d51" });
          }
          initialized.current = true;
        } catch (error) {
          console.error('Error al inicializar Bugsnag:', error);
        }
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return null;
}

// Hook para inicializar Amplitude y su plugin de Session Replay
function useAmplitudeInitialization() {
  const initialized = useRef(false);

  useEffect(() => {
    // Esperar a que la página esté completamente cargada e interactiva
    const onLoad = () => {
      // Usar requestIdleCallback si está disponible, o setTimeout como fallback
      const scheduleInit = window.requestIdleCallback || ((cb) => setTimeout(cb, 5000));
      
      scheduleInit(async () => {
        if (!initialized.current) {
          try {
            const amplitude = await import('@amplitude/analytics-browser');
            const { sessionReplayPlugin } = await import('@amplitude/plugin-session-replay-browser');
            
            const sessionReplay = sessionReplayPlugin({
              serverZone: "EU",
            });
            
            amplitude.add(sessionReplay);
            amplitude.init(AMPLITUDE_API_KEY, {
              autocapture: true,
              serverZone: "EU",
            });
            
            initialized.current = true;
            console.log("Session Replay & Amplitude initialized");
          } catch (error) {
            console.error('Error al inicializar Amplitude:', error);
          }
        }
      });
    };

    // Esperar a que la página termine de cargarse antes de inicializar
    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);
}

// Hook para registrar el Service Worker
function useServiceWorkerRegistration() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener('load', async () => {
        try {
          // Check if window.workbox is available
          if (typeof window.workbox !== 'undefined') {
            const wb = window.workbox;
            
            // Add event listeners to handle updates properly
            wb.addEventListener('waiting', (event) => {
              setWaitingWorker(event.sw);
              setNewVersionAvailable(true);
              
              // Optionally show user notification about the update
              toast.info(
                <div>
                  <p>¡Nueva versión disponible!</p>
                  <button 
                    onClick={() => {
                      wb.messageSkipWaiting();
                      window.location.reload();
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded mt-2 text-sm"
                  >
                    Actualizar ahora
                  </button>
                </div>,
                { 
                  autoClose: false,
                  closeOnClick: false,
                  draggable: false
                }
              );
            });
            
            // Handle controller change (happens after skipWaiting)
            wb.addEventListener('controlling', () => {
              window.location.reload();
            });
            
            // Register the service worker - important to call this explicitly
            wb.register();
            console.log("Workbox service worker registered successfully");
          } else {
            // Fallback for when workbox-window is not available
            const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
            
            console.log(
              "Service Worker registered with scope:",
              registration.scope
            );
            
            // Check for updates on load
            registration.update();
            
            // Add event listener for new updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setNewVersionAvailable(true);
                  setWaitingWorker(newWorker);
                  
                  toast.info(
                    <div>
                      <p>¡Nueva versión disponible!</p>
                      <button 
                        onClick={() => {
                          newWorker.postMessage({ type: 'SKIP_WAITING' });
                          window.location.reload();
                        }}
                        className="bg-blue-500 text-white px-4 py-2 rounded mt-2 text-sm"
                      >
                        Actualizar ahora
                      </button>
                    </div>,
                    { 
                      autoClose: false,
                      closeOnClick: false,
                      draggable: false
                    }
                  );
                }
              });
            });
          }
        } catch (error) {
          console.error("Error registering service worker:", error);
        }
      });
      
      // Set up periodic service worker updates
      const updateInterval = setInterval(() => {
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CHECK_UPDATE' });
        }
      }, 60 * 60 * 1000); // Check for updates every hour
      
      return () => clearInterval(updateInterval);
    }
  }, []);
  
  return { newVersionAvailable, waitingWorker };
}

/* ============================
   COMPONENTE DE INICIALIZACIÓN
   ============================ */

function UserInitializer() {
  const { user, isLoading } = useUser();
  const identified = useRef(false);

  useEffect(() => {
    if (!isLoading && user && !identified.current) {
      // Usar un timeout para no bloquear la interactividad
      setTimeout(async () => {
        try {
          // Cargar Amplitude solo cuando necesitemos identificar al usuario
          const amplitude = await import('@amplitude/analytics-browser');
          
          // Configuración e identificación en Amplitude
          amplitude.setUserId(user.sub);
          const identifyEvent = new amplitude.Identify()
            .set("email", user.email)
            .set("name", user.name)
            .set("role", user.user_roles?.[0] || "none")
            .set("company", user.user_metadata?.companyName || "none");
  
          amplitude.identify(identifyEvent);
          identified.current = true; // Evita re-identificación
  
          console.log("Usuario identificado correctamente.");
        } catch (error) {
          console.error('Error al identificar usuario:', error);
        }
      }, 2000);
    }
  }, [user, isLoading]);

  return null; // Este componente no renderiza nada
}

/* ============================
   PWA INSTALLATION PROMPT
   ============================ */

function PWAInstallPrompt() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [installDismissed, setInstallDismissed] = useState(false);
  
  // Check if user has already dismissed the prompt recently
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const now = new Date().getTime();
      
      // If dismissed in the last 3 days, don't show prompt
      if (now - dismissedTime < 3 * 24 * 60 * 60 * 1000) {
        setInstallDismissed(true);
      } else {
        // Reset dismissal after 3 days
        localStorage.removeItem('pwa-install-dismissed');
      }
    }
  }, []);
  
  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handler = (e) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setInstallEvent(e);
      // Show install prompt if not recently dismissed
      if (!installDismissed) {
        setShowInstallPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallPrompt(false);
    }
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [installDismissed]);
  
  // Listen for app installed event
  useEffect(() => {
    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      // Optionally track installation
      if (typeof amplitude !== 'undefined') {
        amplitude.track('PWA Installed');
      }
      toast.success('¡Gracias por instalar PLAVED!');
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);
  
  const installApp = () => {
    if (!installEvent) return;
    
    // Show the install prompt
    installEvent.prompt();
    
    // Wait for the user to respond to the prompt
    installEvent.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        if (typeof amplitude !== 'undefined') {
          amplitude.track('PWA Install Accepted');
        }
      } else {
        console.log('User dismissed the install prompt');
        if (typeof amplitude !== 'undefined') {
          amplitude.track('PWA Install Rejected');
        }
      }
      
      // Clear the saved prompt since it can't be used again
      setInstallEvent(null);
      setShowInstallPrompt(false);
    });
  };
  
  const dismissPrompt = () => {
    setShowInstallPrompt(false);
    // Save dismissal timestamp
    localStorage.setItem('pwa-install-dismissed', new Date().getTime().toString());
    if (typeof amplitude !== 'undefined') {
      amplitude.track('PWA Install Prompt Dismissed');
    }
  };
  
  if (!showInstallPrompt) return null;
  
  return (
    <div className="fixed bottom-4 left-4 right-4 bg-white p-4 rounded-lg shadow-lg z-50 md:left-auto md:right-4 md:w-96">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          <div className="bg-primary-50 p-2 rounded-full mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="font-semibold text-gray-800">Instalar PLAVED</h3>
        </div>
        <button 
          onClick={dismissPrompt}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Cerrar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
      
      <p className="text-sm text-gray-600 mb-4">
        Añade PLAVED a tu pantalla de inicio para acceder más rápido y disfrutar de todas sus funciones, incluso sin conexión.
      </p>
      
      <div className="flex space-x-3">
        <Button
          onClick={installApp}
          primary
          lg
          full
          amplitudeEventName="PWA Install Button Clicked"
        >
          Instalar
        </Button>
        <Button
          onClick={dismissPrompt}
          tertiary
          lg
          full
          amplitudeEventName="PWA Install Dismissed"
        >
          Más tarde
        </Button>
      </div>
    </div>
  );
}

/* ============================
   COMPONENTE PRINCIPAL (APP)
   ============================ */

const Plaved = ({ Component, pageProps }) => {
  const isLoading = useRouteLoading();

  // Inicialización de Amplitude y registro del Service Worker
  useAmplitudeInitialization();
  const { newVersionAvailable } = useServiceWorkerRegistration();

  return (
    <React.Fragment>
      <UserProvider>
        <UserInitializer />

          <AppWrapper>
            <Head>
              <title>PLAVED</title>
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
              />
            </Head>

            {isLoading && <LoadingSpinner />}

            <ToastContainer
              position="top-left"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />

            <Component {...pageProps} />
            <PWAInstallPrompt />
            <SpeedInsights />
          </AppWrapper>
      </UserProvider>
    </React.Fragment>
  );
};

export default Plaved;